import React from "react";
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Paper,
  Badge,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  MoreVert as MoreVertIcon,
  Assignment as ItemsIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
} from "@mui/icons-material";
import {
  getTemplateDisplayName,
  getTemplateStats,
  formatTemplateForDisplay,
} from "../../models/Template";

/**
 * Enhanced template item component for displaying templates in lists or grids
 *
 * @param {Object} props
 * @param {Object} props.template - Template data using new Template model
 * @param {Function} props.onClick - Callback when the template is clicked
 * @param {Function} props.onMenuClick - Callback when the menu button is clicked
 * @param {Function} props.onToggleFavorite - Callback when the favorite button is clicked
 * @param {boolean} props.showStats - Whether to show template statistics
 * @param {boolean} props.compact - Whether to use compact layout
 */
const TemplateItem = ({
  template,
  onClick,
  onMenuClick,
  onToggleFavorite,
  showStats = true,
  compact = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (!template) {
    return null;
  }

  const displayData = formatTemplateForDisplay(template);
  const stats = getTemplateStats(template);

  const getCategoryColor = (category) => {
    const colorMap = {
      general: "default",
      safety: "error",
      maintenance: "success",
      inspection: "primary",
      quality: "info",
      compliance: "secondary",
      training: "warning",
      audit: "warning",
    };
    return colorMap[category] || "default";
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: compact ? 1.5 : 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: template.isActive ? "divider" : "error.light",
        transition: "all 0.2s ease-in-out",
        opacity: template.isActive ? 1 : 0.7,
        "&:hover": {
          boxShadow: 2,
          borderColor: template.isActive ? "primary.main" : "error.main",
          transform: "translateY(-1px)",
        },
      }}
    >
      {/* Header with category and actions */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          {" "}
          {displayData.category.length > 0 && (
            <Chip
              label={
                displayData.category.charAt(0).toUpperCase() +
                displayData.category.slice(1)
              }
              color={getCategoryColor(displayData.category)}
              size="small"
              variant="outlined"
            />
          )}
          {!compact && (
            <Chip
              label={`v${displayData.version}`}
              color="default"
              variant="outlined"
              size="small"
            />
          )}
          {!template.isActive && (
            <Tooltip title="Template is inactive">
              <InactiveIcon color="error" fontSize="small" />
            </Tooltip>
          )}
          {displayData.id && (
            <Chip
              label={`ID: ${displayData.id}`}
              color="default"
              variant="outlined"
              size="small"
            />
          )}
        </Box>

        <Box sx={{ display: "flex" }}>
          {/* <Tooltip
            title={
              template.isFavorite ? "Remove from favorites" : "Add to favorites"
            }
          >
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onToggleFavorite(template.id);
              }}
              color={template.isFavorite ? "warning" : "default"}
            >
              {template.isFavorite ? (
                <StarIcon fontSize="small" />
              ) : (
                <StarBorderIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip> */}

          {/* <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onMenuClick(e, template);
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton> */}
        </Box>
      </Box>

      {/* Template content */}
      <Box
        sx={{
          flexGrow: 1,
          cursor: "pointer",
        }}
        onClick={() => onClick(template)}
      >
        <Typography
          variant={compact ? "subtitle1" : "h6"}
          component="h3"
          gutterBottom
          sx={{
            fontWeight: 600,
            fontSize: isMobile ? "1rem" : compact ? "1.1rem" : "1.25rem",
            lineHeight: 1.3,
          }}
        >
          {getTemplateDisplayName(template)}
        </Typography>

        {template.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 2,
              display: "-webkit-box",
              overflow: "hidden",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: compact ? 2 : 3,
              lineHeight: 1.4,
            }}
          >
            {template.description}
          </Typography>
        )}

        {/* Tags */}
        {!compact && displayData.tags.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
            {displayData.tags.slice(0, 3).map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                variant="outlined"
                color="primary"
                sx={{ fontSize: "0.7rem", height: 20 }}
              />
            ))}
            {displayData.tags.length > 3 && (
              <Chip
                label={`+${displayData.tags.length - 3}`}
                size="small"
                variant="outlined"
                color="default"
                sx={{ fontSize: "0.7rem", height: 20 }}
              />
            )}
          </Box>
        )}
      </Box>

      {/* Footer with statistics */}
      <Box sx={{ mt: "auto", pt: 1 }}>
        {showStats ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Created {formatDate(template.createdAt)}
            </Typography>

            <Typography variant="caption" color="text.secondary">
              Updated {formatDate(template.updatedAt)}
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {stats.totalItems} {stats.totalItems === 1 ? "item" : "items"}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

export default TemplateItem;
